import { useEffect, type ReactNode } from 'react'
import { Routes, Route, Link, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { LogIn } from 'lucide-react'
import type { RootState } from './store/store'
import MainPage from './pages/MainPage'
import AppPage from './pages/AppPage'
import RequestPage from './pages/RequestPage'
import StatusPage from './pages/StatusPage'
import UserMenu, { login } from './components/UserMenu'
import AppHelpWidget from './components/AppHelpWidget'

/** LoopBuilder is internal: routes render only for a signed-in @replay.io user. */
function AuthGate({ children }: { children: ReactNode }) {
  const { user, checked, unavailable, accessDenied } = useSelector((state: RootState) => state.auth)
  if (user) return children
  if (!checked) return null
  if (accessDenied) {
    return (
      <div className="auth-gate" data-testid="auth-gate-access-denied">
        LoopBuilder is an internal tool. It requires a verified @replay.io account.
      </div>
    )
  }
  if (unavailable) {
    return <div className="auth-gate" data-testid="auth-gate-reconnecting">Reconnecting…</div>
  }
  return (
    <div className="auth-gate" data-testid="auth-gate-sign-in">
      <p>Sign in with your @replay.io account to use LoopBuilder.</p>
      <button
        className="auth-gate__sign-in"
        data-testid="auth-gate-sign-in-button"
        onClick={() => login(window.location.pathname)}
      >
        <LogIn size={16} />
        Sign In
      </button>
    </div>
  )
}

function App() {
  const [searchParams, setSearchParams] = useSearchParams()
  const signedIn = useSelector((state: RootState) => state.auth.user !== null)

  useEffect(() => {
    if (searchParams.has('code') || searchParams.has('state')) {
      const cleaned = new URLSearchParams(searchParams)
      cleaned.delete('code')
      cleaned.delete('state')
      setSearchParams(cleaned, { replace: true })
    }
  }, [searchParams, setSearchParams])

  return (
    <div className="app-shell">
      <header className="app-shell__header">
        <Link to="/" className="app-shell__logo-link">
          <img src="/images/logo.svg" alt="" className="app-shell__logo-mark" width="22" height="26" />
          <span className="app-shell__logo-text">Replay Loop Builder</span>
        </Link>
        <UserMenu />
      </header>
      <main className="app-shell__main">
        <AuthGate>
          <Routes>
            <Route path="/" element={<MainPage />} />
            <Route path="/apps/:id" element={<AppPage />} />
            <Route path="/request" element={<RequestPage />} />
            <Route path="/status" element={<StatusPage />} />
          </Routes>
        </AuthGate>
      </main>
      {signedIn && <AppHelpWidget />}
    </div>
  )
}

export default App
